import { DateTime } from 'luxon';

/**
 * Calculate the open/closed status of a fund centre based on local time
 * @param {string} ianaTimezone - IANA timezone (e.g., 'Asia/Colombo', 'Europe/London')
 * @param {string} gmtTimezone - Fallback GMT timezone if IANA is not available
 * @param {boolean} dstObserved - Whether DST is observed
 * @param {number} openHour - Opening hour (default: 9)
 * @param {number} closeHour - Closing hour (default: 17)
 * @returns {Object} { status: 'open'|'closed'|'opening'|'closing', localTime: string, utcOffset: string }
 */
export function getFundCentreStatus(ianaTimezone, gmtTimezone, dstObserved = false, openHour = 9, closeHour = 17) {
  try {
    // Use IANA timezone if available, otherwise fallback to GMT
    let timezone = ianaTimezone;
    
    if (!timezone && gmtTimezone) {
      // Convert GMT timezone to IANA format if possible
      // This is a simplified conversion - in production, you'd want a proper mapping
      timezone = convertGMTToIANA(gmtTimezone);
    }

    if (!timezone) {
      return {
        status: 'unknown',
        localTime: 'N/A',
        utcOffset: 'N/A',
        error: 'No timezone information available'
      };
    }

    // Get current time in the fund centre's timezone
    const localTime = DateTime.now().setZone(timezone);
    
    if (!localTime.isValid) {
      return {
        status: 'unknown',
        localTime: 'Invalid timezone',
        utcOffset: 'N/A',
        error: localTime.invalidReason
      };
    }

    // Get UTC offset
    const utcOffset = localTime.toFormat('ZZZZ'); // e.g., "+05:30"
    
    // Get current hour and minute
    const currentHour = localTime.hour;
    const currentMinute = localTime.minute;
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    
    // Opening and closing times in minutes
    const openTimeInMinutes = openHour * 60;
    const closeTimeInMinutes = closeHour * 60;
    
    // Calculate time until opening/closing (in minutes)
    const minutesUntilOpen = openTimeInMinutes - currentTimeInMinutes;
    const minutesUntilClose = closeTimeInMinutes - currentTimeInMinutes;
    
    // Determine status
    let status;
    if (currentTimeInMinutes >= openTimeInMinutes && currentTimeInMinutes < closeTimeInMinutes) {
      // Currently open
      if (minutesUntilClose <= 60) {
        status = 'closing'; // Closing within 1 hour
      } else {
        status = 'open';
      }
    } else {
      // Currently closed
      if (currentTimeInMinutes < openTimeInMinutes && minutesUntilOpen <= 60) {
        status = 'opening'; // Opening within 1 hour
      } else {
        status = 'closed';
      }
    }
    
    return {
      status,
      localTime: localTime.toFormat('HH:mm:ss'),
      localDateTime: localTime.toFormat('yyyy-MM-dd HH:mm:ss'),
      utcOffset,
      timezone: timezone
    };
  } catch (error) {
    console.error('Error calculating fund centre status:', error);
    return {
      status: 'unknown',
      localTime: 'Error',
      utcOffset: 'N/A',
      error: error.message
    };
  }
}

/**
 * Convert GMT timezone string to IANA timezone (simplified mapping)
 * @param {string} gmtTimezone - GMT timezone (e.g., 'GMT+5:30', 'GMT-5')
 * @returns {string|null} IANA timezone or null
 */
function convertGMTToIANA(gmtTimezone) {
  // This is a simplified mapping - you may need to expand this
  const gmtToIANA = {
    'GMT+0': 'UTC',
    'GMT+1': 'Europe/London', // Note: London uses BST in summer
    'GMT+2': 'Europe/Berlin',
    'GMT+3': 'Europe/Moscow',
    'GMT+4': 'Asia/Dubai',
    'GMT+5': 'Asia/Karachi',
    'GMT+5:30': 'Asia/Kolkata',
    'GMT+6': 'Asia/Dhaka',
    'GMT+7': 'Asia/Bangkok',
    'GMT+8': 'Asia/Singapore',
    'GMT+9': 'Asia/Tokyo',
    'GMT+10': 'Australia/Sydney',
    'GMT+11': 'Pacific/Auckland',
    'GMT+12': 'Pacific/Fiji',
    'GMT-1': 'Atlantic/Azores',
    'GMT-2': 'Atlantic/South_Georgia',
    'GMT-3': 'America/Sao_Paulo',
    'GMT-4': 'America/Caracas',
    'GMT-5': 'America/New_York',
    'GMT-6': 'America/Chicago',
    'GMT-7': 'America/Denver',
    'GMT-8': 'America/Los_Angeles',
    'GMT-9': 'America/Anchorage',
    'GMT-10': 'Pacific/Honolulu',
    'GMT-11': 'Pacific/Midway',
    'GMT-12': 'Pacific/Kwajalein'
  };
  
  return gmtToIANA[gmtTimezone] || null;
}

/**
 * Get marker color based on status
 * @param {string} status - Status ('open', 'closed', 'opening', 'closing')
 * @returns {string} Color code
 */
export function getStatusColor(status) {
  const colors = {
    'open': '#22c55e',      // Green
    'closed': '#ef4444',    // Red
    'opening': '#f59e0b',   // Amber
    'closing': '#f59e0b',   // Amber
    'unknown': '#6b7280'    // Gray
  };
  
  return colors[status] || colors.unknown;
}

/**
 * Get status label
 * @param {string} status - Status
 * @returns {string} Human-readable label
 */
export function getStatusLabel(status) {
  const labels = {
    'open': 'OPEN',
    'closed': 'CLOSED',
    'opening': 'OPENING SOON',
    'closing': 'CLOSING SOON',
    'unknown': 'UNKNOWN'
  };
  
  return labels[status] || labels.unknown;
}
